import { Box } from '@leagueplatform/genesis-core';
import { getAuthSelectorSearch } from 'utils/auth-connection';
import React, { useEffect } from 'react';
import {
  ErrorState,
  ImpersonationTopBanner,
  LoadingIndicator,
} from '@leagueplatform/web-common-components';
import { getImpersonationType, useAuth } from '@leagueplatform/auth-utils';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { PrivateRoutesContainer } from 'components/private-routes-container/private-routes-container.component';
import { RewardsEventController } from '@leagueplatform/rewards-events';
import { endAgentSession } from 'utils/agent-session';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';

export const AuthSessionContainer = () => {
  const { isAuthenticated, error } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      isAuthenticated === false &&
      !error &&
      location.pathname !== APP_MANAGED_ROUTES.SIGN_IN
    ) {
      const search = getAuthSelectorSearch(location.search);
      navigate(
        {
          pathname: APP_MANAGED_ROUTES.SIGN_IN,
          search: search ? `?${search}` : '',
        },
        {
          replace: true,
          state: {
            redirectPath: `${location.pathname}${location.search}${location.hash}`,
          },
        },
      );
    }
  }, [isAuthenticated, error, navigate, location]);

  // A member signed in, so any agent flag left in this tab is stale and would
  // make a later sign-out without a live session skip `federated`. Checked only
  // once authenticated: before that the claims are missing and every session
  // would read as a member, clearing a real agent's flag mid-callback.
  useEffect(() => {
    if (!isAuthenticated) return;
    getImpersonationType().then((impersonationType) => {
      if (impersonationType === false) endAgentSession();
    });
  }, [isAuthenticated]);

  if (error) {
    return (
      <Box role="alert">
        <ErrorState error={null} hideButton />
      </Box>
    );
  }
  if (!isAuthenticated) return <LoadingIndicator />;

  // The support-agent banner is mounted here, once, rather than in each page
  // shell: onboarding screens (the start-up plan picker included) render in
  // place of BasePage, and an agent on any of them still needs the view-only
  // notice and the control that ends the session. It has to mount after sign-in
  // because the SDK banner reads the ID token claims only once, on mount; any
  // earlier and it misses the token the login callback stores. It renders
  // nothing outside an agent session.
  return (
    <>
      <ImpersonationTopBanner />
      <PrivateRoutesContainer>
        <Outlet />
        <RewardsEventController />
      </PrivateRoutesContainer>
    </>
  );
};
